<template>
  <el-dialog
    :title="'Lokale inwestycji: ' + (investmentName || '')"
    :visible="visible"
    width="1000px"
    :close-on-click-modal="false"
    @update:visible="$emit('update:visible', $event)"
  >
    <el-table v-loading="loading" :data="units" border fit style="width: 100%;">
      <el-table-column label="Lokal" width="140">
        <template slot-scope="scope">
          <span>{{ scope.row.unit_number || scope.row.unit_ref || '-' }}</span>
          <div class="sub-text">{{ buildingLabel(scope.row) }}</div>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Typ" width="100">
        <template slot-scope="scope">
          <span>{{ scope.row.transaction === 'rent' ? 'Wynajem' : 'Sprzedaż' }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Metraż" width="90">
        <template slot-scope="scope">
          <span>{{ scope.row.area ? scope.row.area + ' m2' : '-' }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Pokoje" width="80">
        <template slot-scope="scope">
          <span>{{ scope.row.rooms || '-' }}</span>
        </template>
      </el-table-column>

      <el-table-column align="right" label="Cena" width="130">
        <template slot-scope="scope">
          <span>{{ formatPrice(scope.row.price) }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Status lokalu" width="120">
        <template slot-scope="scope">
          <el-tag :type="unitStatusType(scope.row.unit_status)" size="mini">
            {{ unitStatusLabel(scope.row) }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Ogłoszenie" width="180">
        <template slot-scope="scope">
          <div>{{ offerStatusLabel(scope.row.offer_status) }}</div>
          <div v-if="!scope.row.standalone" class="sub-text">tylko w tabeli lokali</div>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Akcja" width="180">
        <template slot-scope="scope">
          <router-link
            v-if="scope.row.edit_slug"
            :to="{ path: '/offers/edit', query: { slug: scope.row.edit_slug }}"
            target="_blank"
          >
            <el-button type="primary" size="mini" icon="el-icon-edit">Edytuj</el-button>
          </router-link>
          <el-button
            type="danger"
            size="mini"
            icon="el-icon-delete"
            :loading="deletingId === scope.row.id"
            @click="removeUnit(scope.row)"
          >
            Usuń
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <p class="hint">
      Lokal inwestycji to zwykłe ogłoszenie - „Edytuj” otwiera je w edytorze ofert
      (w nowej karcie). Usunięcie lokalu kasuje ogłoszenie i jest nieodwracalne.
    </p>

    <span slot="footer">
      <el-button @click="$emit('update:visible', false)">Zamknij</el-button>
    </span>
  </el-dialog>
</template>

<script>
import InvestmentResource from '@/api/investment';

const investmentResource = new InvestmentResource();

/**
 * Podgląd lokali inwestycji w adminie (GET admin/investments/{id}/units -
 * WSZYSTKIE lokale, także czekające na moderację i sprzedane). Edycja lokalu
 * idzie do istniejącego edytora ofert (lokal to zwykły Offer), usunięcie do
 * DELETE admin/investments/{id}/units/{offerId} (InvestmentManager::destroyUnit).
 * Ceny przychodzą w GROSZACH - dzielimy /100 jak przy zwykłych ofertach.
 */
export default {
  name: 'InvestmentUnitsDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    investmentId: {
      type: String,
      default: null,
    },
    investmentName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      deletingId: null,
      units: [],
    };
  },
  watch: {
    visible(open) {
      if (open) {
        this.load();
      } else {
        this.units = [];
      }
    },
  },
  methods: {
    async load() {
      if (!this.investmentId) {
        return;
      }
      this.loading = true;
      try {
        this.units = await investmentResource.units(this.investmentId);
      } finally {
        this.loading = false;
      }
    },
    buildingLabel(row) {
      const parts = [];
      if (row.unit_building) {
        parts.push('bud. ' + row.unit_building);
      }
      if (row.unit_staircase) {
        parts.push('kl. ' + row.unit_staircase);
      }
      return parts.join(', ');
    },
    formatPrice(grosze) {
      if (!grosze) {
        return '-';
      }
      return new Intl.NumberFormat('pl-PL').format(Math.round(grosze / 100)) + ' zł';
    },
    unitStatusLabel(row) {
      if (row.unit_status === 'sold') {
        return row.transaction === 'rent' ? 'Wynajęte' : 'Sprzedane';
      }
      return row.unit_status === 'reserved' ? 'Zarezerwowane' : 'Dostępne';
    },
    unitStatusType(status) {
      if (status === 'sold') {
        return 'info';
      }
      return status === 'reserved' ? 'warning' : 'success';
    },
    offerStatusLabel(status) {
      const map = {
        active: 'Aktywne',
        in_active: 'Czeka na akceptację',
        in_active_date: 'Publikacja odroczona',
        in_active_by_user: 'Wyłączone',
        pending: 'Czeka na płatność',
        rejected: 'Odrzucone',
        expired: 'Wygasłe',
      };
      return map[status] || status || '-';
    },
    async removeUnit(row) {
      const label = row.unit_number || row.unit_ref || 'lokal';
      try {
        await this.$confirm(
          `Usunąć lokal ${label}? Ogłoszenie lokalu zostanie skasowane. Operacji nie da się cofnąć.`,
          'Usuwanie lokalu',
          { confirmButtonText: 'Usuń lokal', cancelButtonText: 'Anuluj', type: 'warning' }
        );
      } catch (e) {
        return;
      }

      this.deletingId = row.id;
      try {
        await investmentResource.destroyUnit(this.investmentId, row.id);
        this.units = this.units.filter((unit) => unit.id !== row.id);
        this.$notify({ title: 'Sukces', message: 'Lokal został usunięty', type: 'success', duration: 2000 });
        this.$emit('units-changed');
      } finally {
        this.deletingId = null;
      }
    },
  },
};
</script>

<style scoped>
.sub-text {
  font-size: 12px;
  color: #909399;
}
.hint {
  font-size: 12px;
  color: #909399;
  margin: 10px 0 0;
}
</style>
